import React, { useState, Suspense } from "react";
import { Box, useTheme, Alert, CircularProgress } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import Sidebar from "../components/Sidebar";
import WelcomeScreen from "../components/WelcomeScreen";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import { useAuth } from "../context/AuthContext";
import { useThemeMode } from "../context/ThemeContext";
import { Navigate } from "react-router-dom";

const AIStudio = React.lazy(() => import("../pages/AIStudio"));
const SeleniumAssistant = React.lazy(() => import("../pages/MigrationAssistant/SeleniumAssistant"));
const UPAssistant = React.lazy(() => import("../pages/MigrationAssistant/UiPathAssistant"));
const BPAssistant = React.lazy(() => import("../pages/MigrationAssistant/BluePrismAssistant"));
const AAAssistant = React.lazy(() => import("../pages/MigrationAssistant/AAAssistant"));
const BRDStudio = React.lazy(() => import("../pages/BRDStudio"));
const ProposalStudio = React.lazy(() => import("../pages/ProposalStudio"));
const TestCaseStudio = React.lazy(() => import("../pages/TestCaseStudio"));
const AnalyticsDashboard = React.lazy(() => import("../pages/AnalyticsDashboard"));
const CodeReview = React.lazy(() => import("../pages/CodeReview"));
const MoMStudio = React.lazy(() => import("../pages/MoM"));
const SDDStudio = React.lazy(() => import("../pages/SDDStudio"));
const CSMAssistant = React.lazy(() => import("../pages/CSMAssistant"));
const CSMIndia = React.lazy(() => import("../pages/CSMIndia"));
const LogAnalyzer = React.lazy(() => import("../pages/AELogAnalyzer"));
const SupportCopilot = React.lazy(() => import("../pages/SupportCopilot/AELogAnalyzer"));
const TrainingEvalStudio = React.lazy(() => import("../pages/TrainingEvalStudio"));
const BAAssistStudio = React.lazy(() => import("../pages/BAAssist"));
const EmailAutomationStudio = React.lazy(() => import("../pages/EmailAutomation/EmailAutomation"));

const SIDEBAR_WIDTH = 268;
const SIDEBAR_COLLAPSED = 68;

export default function DashboardLayout() {
  const { user, refreshUser } = useAuth();
  const { mode } = useThemeMode();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("lg")); // < 1200px

  // Dynamic sidebar constants
  const dynamicSidebarWidth = isTablet ? 240 : SIDEBAR_WIDTH;

  const [activeTool, setActiveTool] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);

  // Reset warning dismissed if remaining tokens go back above 20k
  React.useEffect(() => {
    if (user && user.remaining_total_tokens !== undefined && user.remaining_total_tokens >= 20000) {
      setWarningDismissed(false);
    }
  }, [user?.remaining_total_tokens]);

  // Auto-collapse on smaller viewports
  React.useEffect(() => {
    if (isTablet) {
      setSidebarCollapsed(true);
    }
  }, [isTablet]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const openToolFromCurrentCategory = (tool) => {
    setActiveTool(tool);
  };

  const openStandaloneTool = (tool) => {
    setActiveCategory(null);
    setActiveTool(tool);
  };

  const backToTools = () => {
    setActiveTool(null);
    refreshUser?.();
  };

  const renderMainContent = () => {
    if (!activeTool) {
      return (
        <WelcomeScreen
          onSelectTool={openToolFromCurrentCategory}
          activeCategory={activeCategory}
          onSelectCategory={setActiveCategory}
          onOpenChangePassword={() => setChangePasswordOpen(true)}
        />
      );
    }

    switch (activeTool.id) {
      case "dashboard":
        return <AnalyticsDashboard onBack={backToTools} onOpenChangePassword={() => setChangePasswordOpen(true)} />;
      case "brd":
        return <BRDStudio onBack={backToTools} />;
      case "proposal":
        return <ProposalStudio onBack={backToTools} />;
      case "migration-up":
        return <UPAssistant tool={activeTool} onBack={backToTools} />;
      case "migration-bp":
        return <BPAssistant tool={activeTool} onBack={backToTools} />;
      case "migration-aa":
        return <AAAssistant tool={activeTool} onBack={backToTools} />;
      case "migration-selenium":
        return <SeleniumAssistant tool={activeTool} onBack={backToTools} />;
      case "ai-studio":
        return <AIStudio tool={activeTool} onBack={backToTools} />;
      case "code-review":
        return <CodeReview onBack={backToTools} />;

      case "mom":
        return <MoMStudio onBack={backToTools} />;
      case "csm":
        return <CSMAssistant onBack={backToTools} />;
      case "csm-india":
        return <CSMIndia onBack={backToTools} />;
      case "Smart Log Analyzer":
        return <LogAnalyzer onBack={backToTools} />;
      case "support-copilot":
        return <SupportCopilot onBack={backToTools} />;
      case "test-case":
        return <TestCaseStudio onBack={backToTools} />;
      case "Project-Documentation":
        return <SDDStudio key="sdd-brd" initialView="brd" onBack={backToTools} />;
      case "Process-Analysis":
        return <SDDStudio key="sdd-workflow" initialView="workflow" onBack={backToTools} />;
      case "training-eval":
        return <TrainingEvalStudio onBack={backToTools} />;
      case "ba-assist":
        return <BAAssistStudio onBack={backToTools} />;
      case "email-automation":
        return <EmailAutomationStudio onBack={backToTools} />;

      default:
        return <AIStudio tool={activeTool} onBack={backToTools} />;
    }
  };

  const bgGradient = mode === 'light'
    ? "radial-gradient(ellipse 80% 60% at 15% 10%, rgba(242,101,34,0.04) 0%, transparent 60%), #f8fafc"
    : "radial-gradient(ellipse 80% 60% at 15% 10%, rgba(242,101,34,0.06) 0%, transparent 60%), #0a1628";

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        background: bgGradient,
      }}
    >
      <Sidebar
        activeTool={activeTool}
        onSelectTool={openStandaloneTool}
        activeCategory={activeCategory}
        onSelectCategory={(cat) => { setActiveTool(null); setActiveCategory(cat); }}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((c) => !c)}
        sidebarWidth={dynamicSidebarWidth}
        sidebarCollapsed={SIDEBAR_COLLAPSED}
        onOpenChangePassword={() => setChangePasswordOpen(true)}
      />

      <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />

      <Box
        component="main"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: "all 0.22s cubic-bezier(0.4,0,0.2,1)",
          p: { xs: 1, sm: 1, md: 1.5 },
          pl: { xs: 0.5, md: 0.75 },
          bgcolor: "transparent",
        }}
      >
        {user && user.remaining_total_tokens !== undefined && user.remaining_total_tokens < 20000 && !warningDismissed && (
          <Alert
            severity="warning"
            onClose={() => setWarningDismissed(true)}
            sx={{
              mb: 1.5,
              borderRadius: "var(--radius)",
              border: "1px solid rgba(242,101,34,0.2)",
              backgroundColor: "rgba(242,101,34,0.1)",
              color: mode === 'light' ? "#b78103" : "#ffb74d",
              fontWeight: "bold",
              "& .MuiAlert-icon": { color: "#F26522" }
            }}
          >
            Warning: Your remaining token balance is critically low ({user.remaining_total_tokens.toLocaleString()} tokens left). Please contact an administrator to increase your limit.
          </Alert>
        )}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: mode === 'light' ? "#ffffff" : "var(--ae-glass)",
            backdropFilter: mode === 'light' ? "none" : "blur(16px)",
            border: `1px solid ${mode === 'light' ? "rgba(0,0,0,0.06)" : "var(--ae-glass-border)"}`,
            borderRadius: { xs: "0px", sm: "var(--radius)" },
            boxShadow: mode === 'light' ? "0 4px 12px rgba(0,0,0,0.05)" : "0 8px 32px rgba(0,0,0,0.2)",
            position: "relative",
          }}
        >
          <Suspense fallback={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%" }}>
              <CircularProgress sx={{ color: "#F26522" }} />
            </Box>
          }>
            {renderMainContent()}
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
}

